'use client'

import { Combobox as BaseCombobox } from '@base-ui/react/combobox'
import { LuCheck, LuSearch } from 'react-icons/lu'

import { cn } from '@/core/utils/common'
import { InputText } from '../input-text'
import { choiceStyles } from './combobox.styles'

import type { ComboboxOption } from './combobox.types'

type ComboboxDropdownProps = {
  searchPlaceholder?: string
  emptyMessage?: string
  label?: string
  errorId?: string
  invalid: boolean
}

export default function ComboboxDropdown({
  searchPlaceholder = '검색…',
  emptyMessage = '검색 결과가 없습니다.',
  label,
  errorId,
  invalid,
}: ComboboxDropdownProps) {
  return (
    <BaseCombobox.Portal>
      <BaseCombobox.Positioner
        align="start"
        sideOffset={6}
        className={choiceStyles.positioner}
      >
        <BaseCombobox.Popup
          aria-label={label ? `${label} 옵션` : '옵션 목록'}
          className={choiceStyles.popup}
        >
          <div className="relative shrink-0 border-b border-foreground/[0.08] p-2">
            <LuSearch
              aria-hidden="true"
              className="pointer-events-none absolute left-5 top-1/2 z-10 size-4 -translate-y-1/2 text-description"
            />
            <BaseCombobox.Input
              render={<InputText classNames={{ input: 'pl-10' }} />}
              aria-label={label ? `${label} 검색` : '옵션 검색'}
              aria-invalid={invalid}
              aria-describedby={errorId}
              placeholder={searchPlaceholder}
              className={cn(
                'cursor-text',
                invalid && 'border-error focus-visible:border-error'
              )}
            />
          </div>

          <BaseCombobox.Empty>
            <div className="px-4 py-4 text-c14 text-description">
              {emptyMessage}
            </div>
          </BaseCombobox.Empty>

          <BaseCombobox.List className={choiceStyles.list}>
            {(option: ComboboxOption, index: number) => (
              <BaseCombobox.Item
                key={option.value}
                value={option}
                index={index}
                disabled={option.disabled}
                className={choiceStyles.item}
              >
                <span className="min-w-0 flex-1 wrap-break-word">
                  {option.label}
                </span>
                <BaseCombobox.ItemIndicator
                  keepMounted
                  className="size-4 shrink-0 opacity-0 data-[selected]:opacity-100"
                >
                  <LuCheck aria-hidden="true" className="h-4 w-4 text-point" />
                </BaseCombobox.ItemIndicator>
              </BaseCombobox.Item>
            )}
          </BaseCombobox.List>
        </BaseCombobox.Popup>
      </BaseCombobox.Positioner>
    </BaseCombobox.Portal>
  )
}
